import type { RouteObject } from "react-router-dom";
import { lazy } from "react";
import { redirect } from "react-router-dom";
import { addSlashes, withCompanyPrefix } from "../route.utils";
import { Spinner } from "@/components/ui/v2/spinner";
import {
  CompanyOffboarding,
  CompanyOffboardingDetail,
  CompanyOffboardingStart,
  CompanyOffboardingsListPage,
  CompanyOffboardingPendingApprovalsPage,
  CompanyOffboardingFinalSettlementsPage,
} from "@/utils/lazyLoadComponents";
// Lazy on purpose: this page imports COMPANY_OFFBOARDING_PATHS back out of THIS
// module, so an eager import is a cycle. It only worked while this file happened
// to be evaluated first; the moment something pulled the page in earlier (e.g. a
// route file importing routes.payroll.ts), `FinalSettlementDetail` was still in
// its TDZ here and the router threw
// "can't access lexical declaration 'FinalSettlementDetail' before initialization".
const FinalSettlementDetail = lazy(() =>
  import("@/pages/(home)/(company)/payrolls/final-settlements/detail").then(
    (m) => ({ default: m.FinalSettlementDetail }),
  ),
);

// ─── Path Segments ────────────────────────────────────────────────────────────
export const COMPANY_OFFBOARDING_DYNAMIC_ID_KEY = "offboardingId";

export enum COMPANY_OFFBOARDING_PATH_SEGMENTS {
  ROOT              = "offboarding",
  OFFBOARDINGS      = "offboardings",
  PENDING_APPROVALS = "pending-approvals",
  START             = "start",
  DETAIL            = `:${COMPANY_OFFBOARDING_DYNAMIC_ID_KEY}`,
  FINAL_SETTLEMENTS = "final-settlements",
}

// ─── Route Definition ─────────────────────────────────────────────────────────
export const companyOffboardingRoutes: RouteObject = {
  path: COMPANY_OFFBOARDING_PATH_SEGMENTS.ROOT,
  handle: { breadcrumb: "Offboarding" },
  HydrateFallback: Spinner,
  children: [
    // ── Page-level tabs layout: each tab owns its own stats + table ──────────
    {
      Component: CompanyOffboarding,
      handle: { flushContent: true },
      children: [
        {
          index: true,
          loader: () => redirect(COMPANY_OFFBOARDING_PATHS.OFFBOARDINGS),
        },
        {
          path: COMPANY_OFFBOARDING_PATH_SEGMENTS.OFFBOARDINGS,
          handle: { breadcrumb: "All Offboardings" },
          Component: CompanyOffboardingsListPage,
        },
        {
          path: COMPANY_OFFBOARDING_PATH_SEGMENTS.PENDING_APPROVALS,
          handle: { breadcrumb: "Pending Approvals" },
          Component: CompanyOffboardingPendingApprovalsPage,
        },
        {
          // Settlements list tab. The `final-settlements/:id` detail below is a
          // deeper (2-segment) sibling, so the two paths do not collide.
          path: COMPANY_OFFBOARDING_PATH_SEGMENTS.FINAL_SETTLEMENTS,
          handle: { breadcrumb: "Final Settlements" },
          Component: CompanyOffboardingFinalSettlementsPage,
        },
      ],
    },

    // ── Standalone routes (no stats / tabs chrome) ───────────────────────────
    {
      path: COMPANY_OFFBOARDING_PATH_SEGMENTS.START,
      handle: { breadcrumb: "Start Offboarding" },
      Component: CompanyOffboardingStart,
    },
    {
      path: COMPANY_OFFBOARDING_PATH_SEGMENTS.DETAIL,
      handle: { breadcrumb: "Offboarding Detail" },
      Component: CompanyOffboardingDetail,
    },
    {
      path: `${COMPANY_OFFBOARDING_PATH_SEGMENTS.FINAL_SETTLEMENTS}/:id`,
      handle: { breadcrumb: "Settlement Detail" },
      Component: FinalSettlementDetail,
    },
  ],
};

// ─── Resolved Paths ───────────────────────────────────────────────────────────
export const COMPANY_OFFBOARDING_PATHS = Object.freeze({
  ROOT: withCompanyPrefix(addSlashes(COMPANY_OFFBOARDING_PATH_SEGMENTS.ROOT)),
  OFFBOARDINGS: withCompanyPrefix(
    addSlashes(
      COMPANY_OFFBOARDING_PATH_SEGMENTS.ROOT,
      COMPANY_OFFBOARDING_PATH_SEGMENTS.OFFBOARDINGS,
    ),
  ),
  PENDING_APPROVALS: withCompanyPrefix(
    addSlashes(
      COMPANY_OFFBOARDING_PATH_SEGMENTS.ROOT,
      COMPANY_OFFBOARDING_PATH_SEGMENTS.PENDING_APPROVALS,
    ),
  ),
  FINAL_SETTLEMENTS: withCompanyPrefix(
    addSlashes(
      COMPANY_OFFBOARDING_PATH_SEGMENTS.ROOT,
      COMPANY_OFFBOARDING_PATH_SEGMENTS.FINAL_SETTLEMENTS,
    ),
  ),
  START: withCompanyPrefix(
    addSlashes(
      COMPANY_OFFBOARDING_PATH_SEGMENTS.ROOT,
      COMPANY_OFFBOARDING_PATH_SEGMENTS.START,
    ),
  ),
  DETAIL: (id: number | string) =>
    withCompanyPrefix(
      addSlashes(COMPANY_OFFBOARDING_PATH_SEGMENTS.ROOT, String(id)),
    ),
  FINAL_SETTLEMENT_DETAIL: (id: number | string) =>
    withCompanyPrefix(
      addSlashes(
        COMPANY_OFFBOARDING_PATH_SEGMENTS.ROOT,
        COMPANY_OFFBOARDING_PATH_SEGMENTS.FINAL_SETTLEMENTS,
        String(id),
      ),
    ),
});
